import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';

GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.mjs';

export interface CompressedImageResult {
  file: File;
  width: number;
  height: number;
  quality: number;
  fileSizeKB: number;
}

export const canCompressImage = (file: File) => {
  return ['image/png', 'image/jpeg'].includes(file.type);
};

export const canCompressPdf = (file: File) => {
  return ['application/pdf'].includes(file.type);
};

/**
 * Reduce el tamaño y peso de una imagen antes de subirla.
 * @param uploadedFile Archivo original
 * @param maxSize Tamaño máximo en px (ancho/alto)
 * @param maxFileSizeKB Tamaño máximo en KB
 * @param minQuality Calidad mínima permitida
 * @returns Promesa con la imagen comprimida
 */
export function compressImage(uploadedFile: File, maxSize: number = 900, maxFileSizeKB: number = 100, minQuality: number = 0.2): Promise<CompressedImageResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        let quality = 1.0; // Comenzamos con la máxima calidad
        let width = img.width;
        let height = img.height;

        // Reducir tamaño manteniendo proporciones
        if (width > maxSize || height > maxSize) {
          const scaleFactor = maxSize / Math.max(width, height);
          width = Math.floor(width * scaleFactor);
          height = Math.floor(height * scaleFactor);
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const processCompression = () => {
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], uploadedFile.name, { type: uploadedFile.type });
                const fileSizeKB = blob.size / 1024; // Convertir bytes a KB

                if (fileSizeKB > maxFileSizeKB && quality > minQuality) {
                  quality -= 0.1; // Reducir calidad gradualmente
                  processCompression();
                } else {
                  resolve({ file: compressedFile, width, height, quality, fileSizeKB });
                }
              } else {
                reject(new Error('Error al comprimir la imagen'));
              }
            },
            uploadedFile.type,
            quality,
          );
        };

        processCompression();
      };
    };

    reader.onerror = () => reject(new Error('Error al leer el archivo'));

    reader.readAsDataURL(uploadedFile);
  });
}

export function compressPDF(file: File, quality: number = 1): Promise<{ file: File }> {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onload = async () => {
      try {
        const pdfData = new Uint8Array(reader.result as ArrayBuffer);
        const pdf = await getDocument({ data: pdfData }).promise;
        const pdfDoc = await PDFDocument.create();

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const scaleFactor = 2; // Aumentar escala para mayor resolución
          const viewport = page.getViewport({ scale: scaleFactor });

          // Crear un canvas para renderizar la página
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          // Renderizar la página en el canvas
          const renderTask = page.render({ canvasContext: ctx!, viewport });
          await renderTask.promise;

          // Convertir canvas a imagen comprimida
          const imgData = canvas.toDataURL('image/jpeg', quality);
          const jpgImage = await pdfDoc.embedJpg(imgData);

          const newPage = pdfDoc.addPage([viewport.width, viewport.height]);
          newPage.drawImage(jpgImage, { x: 0, y: 0, width: viewport.width, height: viewport.height });
        }

        // Guardar PDF comprimido
        const compressedPdfBytes = await pdfDoc.save();
        const compressedFile = new File([compressedPdfBytes], `compressed_${file.name}`, { type: 'application/pdf' });

        resolve({ file: compressedFile });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Error al leer el PDF'));
    reader.readAsArrayBuffer(file);
  });
}
